'use client';

import { useEffect, useRef, useState } from 'react';
import styles from './LandingHome.module.css';
import { prefersReducedMotion } from './landingData';

const PX = 6; // screen pixels per dither cell
const bayer = (x: number, y: number) => {
  const xx = x & 7, yy = y & 7, xy = xx ^ yy;
  return (((xy & 1) << 5) | ((xx & 1) << 4) | ((xy & 2) << 2) | ((xx & 2) << 1) | ((xy & 4) >> 1) | ((xx & 4) >> 2)) / 64;
};
const hash = (x: number, y: number) => { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); };
const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
const BACK = [9, 2, 3], MID = [204, 0, 51], HI = [255, 219, 214];

function greeting(h: number) {
  if (h < 5) return 'Still up, Rutgers?';
  if (h < 12) return 'Good morning, Rutgers';
  if (h < 17) return 'Good afternoon, Rutgers';
  if (h < 21) return 'Good evening, Rutgers';
  return 'Late night, Rutgers';
}

/** The sky over campus at the visitor's real local time, ordered-dithered into scarlet tones. */
export default function DayBackdrop() {
  const ref = useRef<HTMLCanvasElement>(null);
  const [caption, setCaption] = useState('');

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const forced = Number(new URLSearchParams(window.location.search).get('hour'));
    const hourNow = () => (Number.isFinite(forced) && new URLSearchParams(window.location.search).has('hour') ? forced : new Date().getHours() + new Date().getMinutes() / 60);
    let w = 0, h = 0, img: ImageData, raf = 0, last = 0, visible = true;
    const reduce = prefersReducedMotion();

    const resize = () => {
      w = Math.max(1, Math.ceil(canvas.clientWidth / PX));
      h = Math.max(1, Math.ceil(canvas.clientHeight / PX));
      canvas.width = w;
      canvas.height = h;
      img = ctx.createImageData(w, h);
    };
    const draw = (now: number) => {
      const hr = hourNow();
      const t = clamp((hr - 6) / 14);
      const day = Math.sin(Math.PI * t) ** 0.6 * (hr >= 5.5 && hr <= 20.5 ? 1 : 0);
      const glow = Math.exp(-(((hr - 6) / 1.4) ** 2)) + Math.exp(-(((hr - 19.6) / 1.4) ** 2));
      const night = 1 - clamp(day * 3 + glow);
      const aspect = w / h;
      const sunX = 0.1 + 0.8 * t, sunY = 0.66 - 0.52 * Math.sin(Math.PI * t);
      const moonX = 0.78, moonY = 0.26;
      const seconds = now / 1000;
      const horizon = 0.72;
      for (let y = 0; y < h; y++) {
        const fy = y / h;
        for (let x = 0; x < w; x++) {
          const fx = x / w;
          const col = Math.floor(fx * 46);
          const bh = 0.06 + hash(col, 3) * 0.16 + (hash(col, 9) > 0.86 ? 0.1 : 0);
          let v: number;
          if (fy > horizon - bh && fy > horizon - 0.3) {
            // skyline: dark silhouettes with a few lit windows after dark
            const wx = Math.floor((fx * 46 - col) * 4), wy = Math.floor(fy * h);
            v = night > 0.35 && wx % 2 === 0 && wy % 3 === 0 && hash(col * 7 + wx, wy) > 0.72 ? 0.85 : 0;
            if (fy > horizon) v = 0;
          } else if (fy > horizon) {
            v = 0.04;
          } else {
            const up = 1 - fy / horizon; // 0 at the horizon, 1 at the top
            v = 0.1 + day * (0.2 + 0.26 * (1 - up)) + glow * 0.5 * (1 - up) ** 2;
            const dsx = (fx - sunX) * aspect, dsy = fy - sunY;
            v += day > 0.05 || glow > 0.1 ? 0.95 * Math.exp(-(dsx * dsx + dsy * dsy) * 120) * (day * 0.6 + glow) : 0;
            const dmx = (fx - moonX) * aspect, dmy = fy - moonY;
            v += night > 0.3 ? 0.9 * night * Math.exp(-(dmx * dmx + dmy * dmy) * 700) : 0;
            const cloud = Math.sin(fx * 9 + seconds * 0.03 + Math.sin(fy * 6) * 2) * Math.sin(fy * 14 - seconds * 0.02);
            v += Math.max(0, cloud) * 0.1 * (day + glow * 0.5);
            if (night > 0.3 && hash(x, y) > 0.992) v += 0.7 * night;
          }
          const d = bayer(x, y);
          const c = v > d ? (v > 0.78 + d * 0.3 ? HI : MID) : BACK;
          const i = (y * w + x) * 4;
          img.data[i] = c[0]; img.data[i + 1] = c[1]; img.data[i + 2] = c[2]; img.data[i + 3] = 255;
        }
      }
      ctx.putImageData(img, 0, 0);
      const d = new Date();
      setCaption(`${d.toLocaleDateString([], { weekday: 'short' })} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · ${greeting(hr)}`);
    };
    const frame = (now: number) => {
      raf = 0;
      if (!visible || document.hidden) return;
      if (now - last > 120) { last = now; draw(now); }
      if (!reduce) raf = requestAnimationFrame(frame);
    };
    const start = () => { if (!raf) raf = requestAnimationFrame(frame); };
    resize();
    draw(performance.now());
    start();
    window.addEventListener('resize', resize);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; start(); });
    io.observe(canvas);
    document.addEventListener('visibilitychange', start);
    return () => { if (raf) cancelAnimationFrame(raf); window.removeEventListener('resize', resize); io.disconnect(); document.removeEventListener('visibilitychange', start); };
  }, []);

  return (
    <>
      <canvas ref={ref} className={styles.canvas} aria-hidden="true" />
      <p className={styles.backdropCaption} aria-hidden="true">{caption}</p>
    </>
  );
}
